import {WriteIconButton} from '../common/buttons/WriteActions';
import React from 'react';
import {Autocomplete, Box, createFilterOptions, TextField} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {IPackage} from "../../models/Packages/IPackage";
import {PackageGuidDto} from '../../models/Packages/IPackage';
import RestoreIcon from "@mui/icons-material/Restore";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {subOrderSelectorActionSx, subOrderSelectorSx} from './subOrderStyles';

interface PackageSelectorProps {
    options: Array<IPackage | PackageGuidDto> | null;
    value: {
        packageId: number | string;
        name: string;
        quantity: number;
        state?: string;
    };
    excludedNames?: string[];
    label: string;
    onChange: (updatedPackage: { packageId: number | string; name: string; quantity: number }) => void;
    onRemove: () => void;
    onRestore?: () => void;
    isDeleted?: boolean;
    isEditMode?: boolean;
}

const filter = createFilterOptions<IPackage | PackageGuidDto>();

export const PackageSelector: React.FC<PackageSelectorProps> = ({
                                                                    options,
                                                                    value,
                                                                    excludedNames = [],
                                                                    label,
                                                                    onChange,
                                                                    onRemove,
                                                                    onRestore,
                                                                    isDeleted,
                                                                    isEditMode,
                                                                }) => {
    const {t} = useAppTranslation();
    const borderColor = value.packageId ? 'green' : 'warning.main';
    const borderColorQty = value.quantity === 0 ? 'warning.main' : 'green';

    const handlePackageChange = (newValue: IPackage | PackageGuidDto | null) => {
        //if (isEditMode) return; // Блокируем смену пакета в режиме редактирования

        if (newValue) {
            onChange({
                ...value,
                packageId: newValue.id ?? '',
                name: newValue.name
            });
        } else {
            onChange({
                ...value,
                packageId: 0,
                name: ''
            });
        }
    };

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value === '' ? 0 : Number(e.target.value);
        if (val >= 0)
            onChange({...value, quantity: val});
    };

    // Находим текущий выбранный объект из списка для Autocomplete
    const selectedOption = options?.find(opt => opt.id === value.packageId) || null;

    // Фильтруем список доступных опций (исключаем занятые имена, но оставляем текущий выбор)
    const filteredOptions = options?.filter((option) =>
        !excludedNames.includes(option.name) || option.name === value.name
    ) || [];

    return (
        <Box sx={{
            ...subOrderSelectorSx,
            mb: 2,
            opacity: isDeleted ? 0.7 : 1,
            textDecoration: isDeleted ? 'line-through' : 'none',
            backgroundColor: isDeleted ? 'rgba(211,47,47,0.10)' : 'transparent',
            borderRadius: isDeleted ? 1 : 0,
            transition: 'all 0.2s ease-in-out'
        }}>
            <Autocomplete
                id="package_autocomplete"
                options={filteredOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                value={selectedOption}
                //disabled={isEditMode} // Деактивируем Autocomplete в режиме редактирования
                onChange={(_, newValue) => handlePackageChange(newValue)}
                filterOptions={filter}
                sx={{
                    gridArea: 'name',
                    width: '100%',
                    minWidth: 0,
                    '& .MuiAutocomplete-endAdornment': {
                        top: '50%',
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                        transform: 'translateY(-50%)',
                    },
                    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        minHeight: 28,
                        padding: '2px',
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderWidth: '2px',
                            borderColor: borderColor,
                        },
                        '&:hover fieldset': {
                            borderColor: borderColor,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: borderColor,
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        // Передаем ошибку, если нужно (если у вас есть пропс error в компоненте)
                    />
                )}
            />

            <TextField
                type="number"
                label={t('items:count')}
                value={value.quantity}
                onChange={handleQuantityChange}
                sx={{
                    gridArea: 'quantity',
                    width: '100%',
                    minWidth: 0,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px',
                        borderColor: borderColorQty,
                    },
                }}
            />

            <WriteIconButton
                aria-label={t(isDeleted ? 'common:restore' : 'common:delete')}
                color={isDeleted ? "success" : "error"}
                onClick={isDeleted ? onRestore : onRemove}
                size="small"
                sx={subOrderSelectorActionSx}
            >
                {isDeleted ? <RestoreIcon/> : <DeleteOutlineIcon/>}
            </WriteIconButton>
        </Box>
    );
};
